import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import type { GetOrgActionResponse } from 'components/Actions/hooks/orgActionHooks';

export type CustomEventStatus = 'active' | 'monitoring' | 'demobilizing';
export type CustomEventSeverity = 'low' | 'medium' | 'high';
export type CustomEventCategory =
  | 'tropical_storm'
  | 'flood'
  | 'wildfire'
  | 'infrastructure'
  | 'threshold'
  | 'other';

export type CustomEventSummary = {
  id: UUID;
  groupGuid: UUID;
  name: string;
  summary: string | null;
  description: string | null;
  category: CustomEventCategory;
  status: CustomEventStatus;
  severity: CustomEventSeverity;
  startDatetime: string | null;
  expectedEndDatetime: string | null;
  actualEndDatetime: string | null;
  geometry: GeoJSON.Geometry | null;
  archived: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  aors: { id: string; name: string }[];
  resourceSets: { resourceSetId: UUID; name: string | null }[];
};

export type CustomEventActionSeverity = 'low' | 'normal' | 'high' | 'critical';

// Event tasks are org actions
export type CustomEventAction = GetOrgActionResponse & {
  severity: CustomEventActionSeverity | null;
  kind: 'notification' | 'instruction';
  notificationTemplate: { id: UUID; label: string } | null;
};

export type CustomEventDetail = CustomEventSummary & {
  actions: CustomEventAction[];
};

export const customEventKeys = {
  base: ['customEvents'] as const,
  list: (groupGuid: UUID) => [...customEventKeys.base, groupGuid] as const,
  detail: (id: UUID) => [...customEventKeys.base, 'detail', id] as const,
};

export function useCustomEvent(id: UUID, enabled = true) {
  const groupGuid = useGroupGuid();

  return useQuery({
    queryKey: customEventKeys.detail(id),
    queryFn: async () => {
      const { data } = await axios.get<CustomEventDetail>(
        `${config.apiGateway.customEvents}/${id}`,
        { params: { groupGuid } }
      );
      return data;
    },
    enabled: enabled && !!groupGuid && !!id,
  });
}

export type CustomEventActionInput = {
  name: string;
  description?: string | null;
  severity?: CustomEventActionSeverity | null;
  poc?: string | null;
};

export type CreateCustomEventInput = {
  name: string;
  summary?: string | null;
  description?: string | null;
  category: CustomEventCategory;
  status?: CustomEventStatus;
  severity: CustomEventSeverity;
  startDatetime: string;
  expectedEndDatetime?: string | null;
  geometry?: GeoJSON.Geometry | null;
  aorIds?: string[];
  resourceSetIds?: UUID[];
  actions?: CustomEventActionInput[];
};

export function useCreateCustomEvent() {
  const groupGuid = useGroupGuid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCustomEventInput) => {
      const { data } = await axios.post<CustomEventSummary>(
        config.apiGateway.customEvents,
        { ...input, groupGuid }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customEventKeys.base });
    },
  });
}

export function useArchiveCustomEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: UUID) => {
      const { data } = await axios.put<CustomEventSummary>(
        `${config.apiGateway.customEvents}/${id}/archive`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customEventKeys.base });
    },
  });
}

export type UpdateCustomEventInput = Partial<
  Omit<CreateCustomEventInput, 'actions'>
> & {
  actualEndDatetime?: string | null;
};

export function useUpdateCustomEvent(id: UUID) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateCustomEventInput) => {
      const { data } = await axios.put<CustomEventDetail>(
        `${config.apiGateway.customEvents}/${id}`,
        input
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customEventKeys.base }),
  });
}

export function useCustomEvents() {
  const groupGuid = useGroupGuid();

  return useQuery({
    queryKey: customEventKeys.list(groupGuid),
    queryFn: async () => {
      const { data } = await axios.get<CustomEventSummary[]>(
        config.apiGateway.customEvents,
        { params: { groupGuid } }
      );
      return data;
    },
    enabled: !!groupGuid,
  });
}
